import React, { useState, useEffect } from "react";
import { LabeledInput, LabeledTextarea } from "@/Components/Form";
import {
  Formheader,
  GrayBox,
  FormError,
  DateSelectSpan,
  DateSelectDay,
} from "@/Components";
import { MoreVertical, Trash2, XCircle } from "react-feather";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { usePayload } from "@/Contexts/PayloadContext";
import { usePage } from "@inertiajs/inertia-react";
import { addDays } from "date-fns";
import FileUpload from "@/Components/FileUpload";
import { isEmail } from "@/Utilities/Utilities";
import { IconBookMedical } from "@/Icons";
import { atom, useSetAtom } from "jotai/index";

export const isCalculatorAddActivitiesOpenAtom = atom(false);

export default function CalculatorFormPart({ calculator = null }) {
  const { payload, setPayload, onFormChange } = usePayload();
  const errors = usePage().props?.errors || {};

  const [showAllRecipients, setShowAllRecipients] = useState(false);

  const [previewImage, setPreviewImage] = useState(calculator?.logo || null);
  const [previewImageError, setPreviewImageError] = useState("");
  const sensors = useSensors(useSensor(PointerSensor));

  const setOpenModal = useSetAtom(isCalculatorAddActivitiesOpenAtom);

  useEffect(() => {
    if (calculator) {
      setPayload(() => ({
        name: calculator.name,
        start_date: new Date(calculator.start_date),
        end_date: new Date(calculator.end_date),
        expiry_date: new Date(calculator.expiry_date),
        logo_file: false,
        description: calculator.description || "",
        questions: calculator.questions || [],
        recipients: calculator.recipients?.map((r) => r.email) || [],
        recipients_input: "",
        recipients_new: [],
        recipients_delete: [],
      }));
    } else {
      setPayload({
        start_date: new Date(),
        end_date: addDays(new Date(), 7),
        expiry_date: addDays(new Date(), 7),
      });
    }
  }, [calculator]);

  const handlePreviewImage = (e) => {
    setPreviewImageError("");

    const file = e.target.files[0];

    if (file && file["type"].split("/")[0] === "image") {
      // Only allow images to be uploaded
      setPayload((prev) => ({ ...prev, logo_file: file }));
      setPreviewImage(URL.createObjectURL(file));
    } else {
      setPreviewImageError(
        "The selected file type is not allowed. Only jpg, png, svg or webp can be used",
      );
    }
  };

  function handleRemoveImage() {
    setPayload((prev) => ({ ...prev, logo_file: null }));
    setPreviewImage(null);
  }

  function handleKeyDownRecipient(e) {
    if (e.key !== "Enter") {
      return;
    }

    e.preventDefault();

    const emails = payload.recipients_input
      .split(",")
      .map((email) => email.trim().toLowerCase());
    const validEmails = emails.filter((email) => isEmail(email));

    setPayload((prev) => ({
      ...prev,
      recipients: [...(prev.recipients || []), ...validEmails],
      recipients_input: "",
    }));
  }

  function handleRemoveRecipient(email) {
    setPayload((prev) => ({
      ...prev,
      recipients: prev.recipients.filter((e) => e !== email),
      recipients_delete: [...(prev.recipients_delete || []), email],
    }));
  }

  function handleRemoveActivity(id) {
    setPayload((prev) => ({
      ...prev,
      questions: prev.questions.filter((a) => a.id !== id),
    }));
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      setPayload((items) => {
        const oldIndex = items.questions.findIndex(
          ({ id }) => id === active.id,
        );
        const newIndex = items.questions.findIndex(({ id }) => id === over.id);
        const questions = arrayMove(items.questions, oldIndex, newIndex);

        return { ...items, questions };
      });
    }
  }

  function SortableItem({ item, i }) {
    return <InputTypeItem id={item.id} sortIndex={i} item={item} />;
  }

  function InputTypeItem({ item, sortIndex, id }) {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        className="mb-2 flex w-full justify-between bg-white p-4 shadow-small"
        ref={setNodeRef}
        style={style}
      >
        <button
          className="flex cursor-grab"
          {...attributes}
          {...listeners}
          type="button"
        >
          <MoreVertical className="-ml-2" stroke="#668E9F" />
          <MoreVertical className="-ml-4" stroke="#668E9F" />
        </button>
        <div className="flex w-full items-center justify-between">
          <p className="leading-0 mr-4 font-bold">{item.title}</p>
          <Trash2
            className="cursor-pointer"
            onClick={() => handleRemoveActivity(item.id)}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <GrayBox margin="mb-4">
        <div className="flex flex-wrap gap-4">
          <Formheader margin="w-full">About the calculator</Formheader>
          <LabeledInput
            required
            name="name"
            title="Name of the calculator"
            value={payload?.name || ""}
            onChange={onFormChange}
            placeholder="Pick a good name"
            error={errors?.name}
            containerClass="w-full"
          />

          <div className="w-full md:basis-1/2-gap-4">
            <span className="mb-1 block text-[14px]">Reporting period</span>
            <DateSelectSpan
              value={{
                from:
                  payload?.start_date || calculator?.start_date || new Date(),
                to:
                  payload?.end_date ||
                  calculator?.end_date ||
                  addDays(new Date(), 7),
              }}
              buttons={[
                "clear",
                "week",
                "month",
                "year",
                "lastWeek",
                "lastMonth",
                "lastYear",
              ]}
              onChange={(value) => {
                onFormChange({
                  target: { name: "start_date", value: value.from },
                });
                onFormChange({
                  target: { name: "end_date", value: value.to },
                });
              }}
              className="w-full"
              required
            />
            <FormError error={errors && errors.end_date} />
            <FormError error={errors && errors.start_date} />
          </div>

          <div className="w-full md:basis-1/2-gap-4">
            <span className="mb-1 block text-[14px]">
              How long should the link be valid?
            </span>
            <DateSelectDay
              value={
                payload?.expiry_date ||
                calculator?.expiry_date ||
                addDays(new Date(), 7)
              }
              buttons={["clear"]}
              onChange={(date) => {
                onFormChange({
                  target: { name: "expiry_date", value: date },
                });
              }}
              required
            />
            <FormError error={errors && errors.expiry_date} />
          </div>

          <LabeledTextarea
            name="description"
            title="Description"
            value={payload?.description || ""}
            onChange={onFormChange}
            placeholder="Enter calculator description"
            error={errors?.description}
            containerClass="w-full"
          />

          <div className="mt-4 w-full">
            <span className="mb-1 block text-[14px]">Logotype</span>
            {previewImage === null && (
              <FileUpload onFileAdded={handlePreviewImage} />
            )}
            {previewImage && (
              <div className="relative mt-2 flex w-full items-center justify-center gap-2 border border-borderColor bg-gray p-8">
                <img
                  alt="Logo"
                  src={previewImage}
                  className="w-full max-w-[200px] object-scale-down"
                />
                <button
                  onClick={() => handleRemoveImage()}
                  title="Remove image"
                  type="button"
                >
                  <XCircle
                    className="absolute right-2 top-2 scale-100 cursor-pointer p-2 transition-transform hover:scale-110"
                    size={42}
                  />
                </button>
              </div>
            )}
            <FormError error={errors.logo_file || previewImageError} />
          </div>
        </div>
      </GrayBox>

      <GrayBox margin="mb-4">
        <div className="flex flex-wrap gap-4">
          <Formheader className="w-full">Activities</Formheader>

          <button
            onClick={() => setOpenModal(true)}
            type="button"
            className="flex min-h-[46px] w-full items-center gap-2 border border-dashed border-gray-darker px-3 py-2 text-blue underline hover:no-underline"
          >
            <IconBookMedical />
            Add activities
          </button>

          <FormError error={errors?.questions} />

          <div className="w-full">
            <DndContext
              sensors={sensors}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              {payload?.questions && payload.questions.length > 0 && (
                <>
                  <SortableContext
                    items={payload.questions.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {payload.questions.map((item, i) => (
                      <SortableItem key={item.id} item={item} i={i} />
                    ))}
                  </SortableContext>
                </>
              )}
            </DndContext>
          </div>
        </div>
      </GrayBox>

      <GrayBox margin="mb-4">
        <div className="flex flex-wrap gap-4">
          <Formheader className="w-full">Recipients</Formheader>

          <LabeledInput
            name="recipients_input"
            title="Add recipient with email"
            placeholder="Add recipient with email, use comma to add more than one"
            className="mb-4"
            value={payload?.recipients_input || ""}
            onChange={onFormChange}
            onKeyDown={handleKeyDownRecipient}
            error={errors && errors.recipients}
            containerClass="w-full"
          />

          {payload.recipients && (
            <div className="-mt-4 flex flex-wrap gap-2">
              {payload.recipients
                .slice(0, showAllRecipients ? payload.recipients.length : 3)
                .map((email, i) => (
                  <p
                    key={i}
                    className="relative flex items-center justify-between gap-2 bg-blue-lighter px-3 py-2 shadow"
                  >
                    {email}{" "}
                    <XCircle
                      className="cursor-pointer"
                      size={16}
                      onClick={() => handleRemoveRecipient(email)}
                    />
                  </p>
                ))}

              {!showAllRecipients && payload.recipients.length > 3 && (
                <button
                  type="button"
                  className="text-blue-dark underline hover:no-underline"
                  onClick={() => setShowAllRecipients(true)}
                >
                  Show all ({payload.recipients.length - 3})
                </button>
              )}
            </div>
          )}
        </div>
      </GrayBox>
    </>
  );
}
